import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    Typography,
    IconButton,
    Button,
    Avatar,
    Drawer,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    Tooltip,
    Badge,
    List,
    ListItem,
    Divider
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useNavigate } from 'react-router-dom';
import userIcon from '../../assets/usericon.svg';
import { getUserById } from '../../service/user';

const Navbar = () => {
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const notificationOpen = Boolean(notificationAnchorEl);
    const [userDetails, setUserDetails] = useState(null);

    const storedUser = localStorage.getItem("user");
    const user = storedUser ? JSON.parse(storedUser) : null;
    const [patient, setPatient] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);

    const wellnessSubtitles = [
        "🌿 Take 3 slow breaths and reset your mind.",
        "💚 Your wellness matters—pause and check in with yourself.",
        "🧠 Small steps today lead to better mental balance.",
        "🌬️ Inhale calm. Exhale stress.",
        "🕯️ Take a moment of stillness for your mind.",
        "🧘 Mental Health Focus",
        "💙 It’s okay to slow down today.",
        "🧠 Notice one positive thought and hold onto it.",
        "🌱 Be gentle with yourself—you’re doing your best.",
        "💬 Expressing how you feel is a form of strength.",
        "🧘 Your mind deserves rest as much as your body.",
        "☀️ Daily Wellness Tip: Start your day with intention.",
        "🌞 A calm morning sets the tone for the day.",
        "🧘 Morning Tip: Stretch your body, clear your thoughts.",
        "💧 Begin your day hydrated and refreshed.",
        "🌿 One mindful moment can change your whole day.",
        "🌙 Slow down—your day doesn’t have to end in a rush.",
        "😴 Prepare your mind for restful sleep.",
        "🕯️ Reflect on one good thing from today.",
        "🌬️ Let go of today’s stress, gently.",
        "💚 Rest is part of healing.",
        "🎧 Daily Tip: Music can help calm your thoughts—listen mindfully.",
        "💚 Care Tip: Check in with your emotions today.",
        "🧠 Wellness Tip: Awareness is the first step to healing.",
        "🌿 Your mental health matters—one step at a time.",
        "🎶 Sound and silence both heal—choose what you need.",
        "🌿 Breathe. Reset. Continue.",
        "💚 Take care of your mind.",
        "🧠 Pause. Feel. Release.",
        "🌬️ Calm starts with one breath.",
        "🧘 Be present—right now."
    ];

    useEffect(() => {
        // const today = new Date();
        const today = new Date("2026-02-09"); // Monday

        const isMonday = today.getDay() === 1; // Monday = 1
        const currentWeek = getWeekNumber(today);

        const storedWeek = localStorage.getItem("wellnessWeek");
        let selectedSubtitle = localStorage.getItem("weeklyWellnessSubtitle");

        // ✅ Only on Monday AND only if not already set for this week
        if (isMonday && storedWeek !== currentWeek.toString()) {
            const randomIndex = Math.floor(Math.random() * wellnessSubtitles.length);
            selectedSubtitle = wellnessSubtitles[randomIndex];

            localStorage.setItem("weeklyWellnessSubtitle", selectedSubtitle);
            localStorage.setItem("wellnessWeek", currentWeek.toString());

            // Show notification ONLY on Monday
            setNotifications([
                {
                    id: 1,
                    message: selectedSubtitle,
                    time: "Today",
                },
            ]);
        } else {
            // ❌ Tue–Sun → show nothing
            setNotifications([]);
        }
    }, []);

    function getWeekNumber(date) {
        const firstJan = new Date(date.getFullYear(), 0, 1);
        const pastDays = Math.floor((date - firstJan) / 86400000);
        return Math.ceil((pastDays + firstJan.getDay() + 1) / 7);
    }

    const handleNotificationClick = (event) => {
        setNotificationAnchorEl(event.currentTarget);
    };

    const handleNotificationClose = () => {
        setNotificationAnchorEl(null);
    };


    const handleProfile = () => {
        handleClose();
        navigate('/profile');
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleClose();  
        localStorage.removeItem('analytics_data');
        // close any menus/dialogs
        localStorage.removeItem('user'); // remove user datauser_id
        localStorage.removeItem('user_id'); // remove user data
        localStorage.removeItem('userExpiry'); // remove user data
        localStorage.removeItem('conserve_category'); // remove user data
        localStorage.removeItem('account_type'); // remove user data
        localStorage.removeItem('package_type'); // remove user data
        navigate('/', { replace: true }); // redirect to login and replace history
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const storedUser = localStorage.getItem("user");
                if (storedUser) {
                    const user = JSON.parse(storedUser);
                    console.log('Fetching user details for ID:', user.id);
                    const userData = await getUserById(user.id);
                    console.log('User details fetched:', userData);
                    setUserDetails(userData);
                }
            } catch (error) {
                console.error('Error fetching user details:', error);
            }
        };

        fetchUserDetails();
    }, []);

    const drawerContent = (
        <Box sx={{ p: 2, width: 250 }}>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 7 }}>
                <Tooltip title="Notifications" arrow>
                    <IconButton
                        onClick={handleNotificationClick}
                        sx={{ backgroundColor: '#f1f5f9', mr: 1 }}
                    >
                        <Badge
                            badgeContent={notifications.length}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '0.6rem',
                                    height: 16,
                                    minWidth: 16,
                                    padding: '0 4px',
                                    top: 4,
                                    right: 4
                                }
                            }}
                        >
                            <NotificationsIcon sx={{ color: '#4f46e5', width: 20, height: 20, }} />
                        </Badge>
                    </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ fontWeight: 500, cursor: 'pointer' }} onClick={handleNotificationClick}>
                    Notifications
                </Typography>
            </Box>

            <Menu
                anchorEl={notificationAnchorEl}
                open={notificationOpen}
                onClose={handleNotificationClose}
                onClick={handleNotificationClose}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        width: 400,
                        maxWidth: '100%',
                        mt: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)'
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "grey.50"
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600}>
                        Notifications
                    </Typography>
                </Box>

                {/* Notification List */}
                <Box sx={{ maxHeight: 350, overflowY: "auto" }}>
                    {notifications.length > 0 ? (
                        <List disablePadding>
                            {notifications.map((notification) => (
                                <Box key={notification.id}>
                                    <ListItem
                                        sx={{
                                            alignItems: "flex-start",
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                backgroundColor: "action.hover"
                                            },
                                            px: 2,
                                            py: 1.5
                                        }}
                                    >
                                        <Box>
                                            {/* Title */}
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                                sx={{ mb: 0.5 }}
                                            >
                                                Daily Wellness Tip Notifications
                                            </Typography>

                                            {/* Message */}
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {notification.message}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                    <Divider />
                                </Box>
                            ))}
                        </List>
                    ) : (
                        <Box
                            sx={{
                                py: 4,
                                textAlign: "center",
                                color: "text.secondary"
                            }}
                        >
                            <Typography variant="body2">
                                No new notifications
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Menu>

            {/* <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <IconButton onClick={handleClick} disableRipple>
                    <Avatar
                        src={userIcon}
                        sx={{
                            width: 33,
                            height: 33,
                            bgcolor: '#E4E8FF'
                        }}
                    />
                </IconButton>

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Hello {user?.name?.replace(/\b\w/g, (char) => char.toUpperCase()) || "Guest"}
                </Typography>
            </Box> */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <IconButton onClick={handleClick} disableRipple>
                    <Avatar
                        src={
                            userDetails?.gender?.toLowerCase() === 'male' ? '/man.png' :
                                userDetails?.gender?.toLowerCase() === 'female' ? '/woman.png' :
                                    '/user.png'
                        }
                        sx={{
                            width: 33,
                            height: 33,
                            bgcolor: userDetails?.gender?.toLowerCase() === 'male'
                                ? '#E4E8FF'
                                : userDetails?.gender?.toLowerCase() === 'female'
                                    ? '#FFE4E8'
                                    : '#E8E8E8'
                        }}
                    />
                </IconButton>

                <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                    Hello {userDetails?.name?.replace(/\b\w/g, (char) => char.toUpperCase()) || "Guest"}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar
                position="sticky"
                color="transparent"
                elevation={0}
                sx={{
                    top: 0,
                    background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    px: { xs: 1, sm: 2 },
                    backgroundColor: '#fff',
                    height: { xs: 56, sm: 64, md: 70 }, // responsive heights
                    minHeight: { xs: 56, sm: 64, md: 70 },
                }}
            >
                <Toolbar
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'nowrap',
                        gap: 1
                    }}
                >
                    {/* Left: Logo */}

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Link
                            to="/home"
                            style={{
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                position: 'absolute',
                                top: '0.75rem',

                                left: '2px',
                                zIndex: 1000,
                            }}
                        >
                            {/* Logo Image */}
                            <Box
                                component="img"
                                src="/diginxthlt-logo.svg"
                                alt="Echo Logo"
                                sx={{
                                    height: { xs: 35, sm: 40, md: 45 },
                                    width: { xs: 35, sm: 40, md: 45 },
                                    marginTop: '0rem',
                                    mr: 1,
                                }}
                            />

                            {/* Text Section */}
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '1.4rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.5px',
                                        lineHeight: 1.1,
                                        mb: 0,
                                        color: "#7843f3"
                                    }}
                                >
                                    echoBuddy
                                </Typography>

                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 500,
                                        letterSpacing: '0.3px',
                                        display: 'block',
                                        lineHeight: 1.1,
                                        color: "#7843f3"
                                    }}
                                >
                                    Connect. Heal. Rise.
                                </Typography>
                            </Box>
                        </Link>
                    </Box>

                    {/* Right: Buttons and Avatar (Hidden on small screens) */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', ml: 'auto' }}>
                        <Tooltip title="Notifications" arrow>
                            <IconButton
                                onClick={handleNotificationClick}
                                sx={{
                                    ml: 2,
                                    backgroundColor: '#f1f5f9',
                                    '&:hover': { backgroundColor: '#e2e8f0' }
                                }}
                                disableRipple
                            >
                                <Badge
                                    badgeContent={notifications.length}
                                    color="error"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            fontSize: '0.6rem',
                                            height: 16,
                                            minWidth: 16,
                                            padding: '0 4px',
                                            top: 4,
                                            right: 4
                                        }
                                    }}
                                >
                                    <NotificationsIcon sx={{
                                        color: '#22c55e' // or theme color
                                    }} />
                                </Badge>
                            </IconButton>
                        </Tooltip>


                        <>
                            {/* <IconButton onClick={handleClick} sx={{ ml: 2 }} disableRipple>
                                <Avatar
                                    src={userIcon}
                                    sx={{
                                        width: 33,
                                        height: 33,
                                        bgcolor: '#E4E8FF'
                                    }}
                                />
                            </IconButton> */}

                            {/* desktop view */}
                            <IconButton onClick={handleClick} sx={{ ml: 2 }} disableRipple>


                                <Avatar src={
                                    userDetails?.gender?.toLowerCase() === 'male' ? '/man.png' :
                                        userDetails?.gender?.toLowerCase() === 'female' ? '/woman.png' :
                                            '/user.png'
                                }
                                    sx={{
                                        width: 33,
                                        height: 33,
                                        bgcolor: userDetails?.gender?.toLowerCase() === 'male'
                                            ? '#E4E8FF'
                                            : userDetails?.gender?.toLowerCase() === 'female'
                                                ? '#FFE4E8'
                                                : '#E8E8E8'
                                    }}
                                />
                            </IconButton>

                            <Menu
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                PaperProps={{
                                    sx: {
                                        minWidth: 120,
                                        width: '220px',
                                    }
                                }}
                            >
                                <MenuItem onClick={handleProfile}>
                                    Profile
                                </MenuItem>


                                {patient && (
                                    <MenuItem>
                                        {patient.full_name} — {patient.dr_name}
                                    </MenuItem>
                                )}

                                <MenuItem onClick={handleLogout}>
                                    Logout
                                </MenuItem>
                            </Menu>

                            <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                                Hello {user?.name?.replace(/\b\w/g, (char) => char.toUpperCase()) || "Guest"}
                            </Typography>
                        </>
                    </Box>

                    {/* Hamburger Icon (Only on Mobile) */}
                    <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                        <IconButton onClick={handleDrawerToggle}>
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
            >
                {drawerContent}
            </Drawer>


            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Patient Profile</DialogTitle>
                <DialogContent dividers>
                    {patient ? (
                        <>
                            <Typography><strong>Full Name:</strong> {patient.full_name}</Typography>
                            <Typography><strong>Age:</strong> {patient.age}</Typography>
                            <Typography><strong>Gender:</strong> {patient.gender}</Typography>
                            <Typography><strong>Marital Status:</strong> {patient.marital_status}</Typography>
                            <Typography><strong>Consultation Type:</strong> {patient.consultation_type}</Typography>
                            <Typography><strong>Consulting For:</strong> {patient.consulting_type}</Typography>
                            <Typography><strong>Doctor:</strong> {patient.dr_name}</Typography>
                            <Typography><strong>Date:</strong> {new Date(patient.date).toLocaleDateString()}</Typography>
                            <Typography><strong>Time:</strong> {patient.time}</Typography>
                            <Typography><strong>Created At:</strong> {new Date(patient.created_at).toLocaleString()}</Typography>
                        </>
                    ) : (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 150 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => setDialogOpen(false)}>
                        Close
                    </Button>
                </DialogContent>
            </Dialog>

        </>
    );
};

export default Navbar;
